import { useState } from 'react';
import { useOutletContext } from 'react-router';
import { TriangleAlert, UserPlus } from 'lucide-react';
import { NO_TEAM_FILTER, type AdminUser, type Session } from '@product-portal/shared';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/portal/modal';
import { PageTitle } from '@/components/portal/page-title';
import { Pagination } from '@/components/portal/pagination';
import { RolePill } from '@/components/portal/role-pill';
import { SearchField } from '@/components/portal/search-field';
import { SectionCard } from '@/components/portal/section-card';
import { SelectField } from '@/components/portal/select-field';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { UserInviteForm } from '@/features/admin/user-invite-form';
import { NativeSelect } from '@/features/requests/request-form';
import { useAdminUsers, useAttach, useDetach } from '@/hooks/use-admin';
import { formatDate, formatRelative, fullName } from '@/lib/format';
import { ROLE_LABELS } from '@/lib/labels';
import { cn } from '@/lib/utils';

const MATRIX: [string, string, string, string][] = [
  ['Créer et suivre ses demandes', 'Oui', 'Oui', 'Oui'],
  ['Voir les demandes de son équipe', "Si l'option de l'équipe est activée", 'Oui', 'Oui'],
  ['Vue par équipe (écran 06)', '—', 'Lecture seule, son équipe', 'Toutes les équipes'],
  ['Saisir une demande pour un collaborateur', '—', '—', 'Oui'],
  ['Qualifier : statut, priorité, impact retenu', '—', '—', 'Oui'],
  ['Prioriser et publier le backlog', '—', '—', 'Oui'],
  ['Notes internes, ticket Jira', '—', '—', 'Oui'],
  ['Rédiger et publier une release note', '—', '—', 'Oui'],
  ['Administrer : équipes, rattachements, produits, journal', '—', '—', 'Oui'],
];

/** Attachment panel. The role is shown, never editable: it comes from Entra (ADR-002). */
function AttachPanel({ user, teams, onClose }: { user: AdminUser; teams: { id: string; name: string }[]; onClose: () => void }) {
  const attach = useAttach();
  const detach = useDetach();
  const available = teams.filter((t) => !user.teams.some((u) => u.id === t.id));
  const [teamId, setTeamId] = useState(available[0]?.id ?? '');
  return (
    <form
      id="rattacher"
      aria-labelledby="rattacher-titre"
      className="flex flex-col gap-4 rounded-card border border-border bg-card p-5"
      onSubmit={(event) => {
        event.preventDefault();
        if (teamId) attach.mutate({ userId: user.id, teamId });
      }}
    >
      <div className="flex flex-col gap-1">
        <h2 id="rattacher-titre" className="m-0 text-base font-bold">
          Rattacher · {fullName(user)}
        </h2>
        <span className="text-[13px] text-muted-foreground">
          {user.email} · première connexion le {formatDate(user.createdAt)}
        </span>
      </div>
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-bold">Rôle</span>
        <RolePill role={user.displayedRole} />
        <span className="text-xs text-muted-foreground">Pour en faire un référent ou un PO, ajoutez la personne au groupe Entra ID qui porte le rôle.</span>
      </div>
      {user.teams.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-bold">Équipes actuelles</span>
          {user.teams.map((t) => (
            <span key={t.id} className="flex items-center justify-between gap-2 rounded-control border border-border px-3 py-2 text-[13px]">
              {t.name}
              <Button type="button" variant="secondary" size="sm" disabled={detach.isPending} onClick={() => detach.mutate({ userId: user.id, teamId: t.id })}>
                Retirer
              </Button>
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="equipe" className="text-sm font-bold">
          Équipe
          <span aria-hidden="true" className="text-required">
            *
          </span>
        </label>
        <NativeSelect id="equipe" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
          <option value="">Choisir une équipe</option>
          {available.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </NativeSelect>
      </div>
      {(attach.error ?? detach.error) && (
        <p role="alert" className="m-0 text-sm font-bold text-destructive">
          {(attach.error ?? detach.error)!.message}
        </p>
      )}
      <div className="flex justify-between gap-2.5">
        <Button type="button" variant="secondary" onClick={onClose}>
          Fermer
        </Button>
        <Button type="submit" disabled={!teamId || attach.isPending}>
          Rattacher à l'équipe
        </Button>
      </div>
      <span className="text-xs text-muted-foreground">Le changement est écrit dans le journal d'audit.</span>
    </form>
  );
}

/** 11 · Utilisateurs & accès. The portal only manages team attachment; roles are displayed. */
export function UsersPage() {
  const session = useOutletContext<Session>();
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [teamId, setTeamId] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [invited, setInvited] = useState<{ email: string; emailSent: boolean } | null>(null);
  const users = useAdminUsers({ q, role, teamId, page });
  const data = users.data;
  const selected = data?.items.find((u) => u.id === selectedId) ?? null;
  const reset = (set: (v: string) => void) => (v: string) => {
    set(v);
    setPage(1);
  };

  return (
    <>
      <PageTitle title="Utilisateurs & accès" subtitle="Les rôles viennent d'Entra ID et s'affichent ici sans pouvoir être modifiés. Le portail gère le rattachement aux équipes et invite de nouveaux utilisateurs.">
        <Button type="button" onClick={() => setInviting(true)}>
          <UserPlus aria-hidden="true" className="size-4" /> Créer un nouvel utilisateur
        </Button>
      </PageTitle>
      {invited && (
        <p role="status" className="m-0 rounded-control border border-border bg-card px-4 py-3 text-[13px]">
          Compte créé pour <strong>{invited.email}</strong>.{' '}
          {invited.emailSent ? "L'invitation est partie par email." : "L'email d'invitation n'a pas pu partir : prévenez la personne vous-même."} Il sera relié à sa première connexion.
        </p>
      )}
      {inviting && (
        <Modal titleId="invite-titre" title="Nouvel utilisateur" onClose={() => setInviting(false)}>
          <UserInviteForm
            onDone={(result) => {
              setInviting(false);
              setInvited(result);
            }}
          />
        </Modal>
      )}
      {(data?.withoutTeamCount ?? 0) > 0 && (
        <div role="status" className="flex flex-wrap items-center gap-3 rounded-card border border-warning-border bg-warning px-5 py-3.5 text-[13px] text-foreground-secondary">
          <TriangleAlert aria-hidden="true" className="size-[18px] text-warning-foreground" />
          <span className="flex-[1_1_300px]">
            <strong className="text-warning-foreground">
              {data!.withoutTeamCount} compte{data!.withoutTeamCount > 1 ? 's' : ''} sans équipe.
            </strong>{' '}
            Ces personnes se sont connectées mais voient « Accès refusé » tant qu'elles ne sont rattachées à aucune équipe (sauf un PO).
          </span>
          <button type="button" onClick={() => reset(setTeamId)(NO_TEAM_FILTER)} className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[13px] font-bold text-primary">
            Afficher les comptes sans équipe →
          </button>
        </div>
      )}
      <div className="flex flex-wrap items-start gap-6">
        <div className="flex min-w-0 flex-[999_1_640px] flex-col gap-6">
          <SectionCard
            title={`${data?.total ?? 0} utilisateurs`}
            toolbar={
              <>
                <SearchField label="Rechercher un utilisateur" placeholder="Nom ou email" value={q} onChange={reset(setQ)} className="max-w-72" />
                <div className="ml-auto flex flex-wrap gap-2.5">
                  <SelectField label="Rôle" size="sm" value={role} onChange={(e) => reset(setRole)(e.target.value)}>
                    <option value="">Tous les rôles</option>
                    {(['requester', 'referent', 'po'] as const).map((r) => (
                      <option key={r} value={r}>
                        {ROLE_LABELS[r]}
                      </option>
                    ))}
                  </SelectField>
                  <SelectField label="Équipe" size="sm" value={teamId} onChange={(e) => reset(setTeamId)(e.target.value)}>
                    <option value="">Toutes</option>
                    <option value={NO_TEAM_FILTER}>Sans équipe</option>
                    {(data?.teams ?? []).map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </SelectField>
                </div>
              </>
            }
            footer={<Pagination page={page} pageSize={data?.pageSize ?? 20} total={data?.total ?? 0} itemLabel="utilisateurs" onPageChange={setPage} ariaLabel="Pagination des utilisateurs" />}
          >
            <Table minWidth={720}>
              <TableHeader>
                <TableRow>
                  <TableHead>Utilisateur</TableHead>
                  <TableHead>Équipe</TableHead>
                  <TableHead>Rôle (Entra ID)</TableHead>
                  <TableHead>Dernière connexion</TableHead>
                  <TableHead>
                    <span className="sr-only">Action</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.items ?? []).map((u) => (
                  <TableRow key={u.id} className={cn(u.id === selectedId && 'bg-accent')}>
                    <TableCell>
                      <span className="block font-bold">{fullName(u)}</span>
                      <span className="block text-xs text-muted-foreground">{u.email}</span>
                    </TableCell>
                    <TableCell className={cn(u.teams.length === 0 && 'font-bold text-warning-foreground')}>
                      {u.teams.length > 0 ? u.teams.map((t) => t.name).join(', ') : 'Sans équipe'}
                    </TableCell>
                    <TableCell>
                      <RolePill role={u.displayedRole} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-foreground-secondary">{u.lastLoginAt ? formatRelative(u.lastLoginAt) : u.invited ? 'Invité, jamais connecté' : '—'}</TableCell>
                    <TableCell>
                      {u.id === session.user.id ? (
                        <span className="text-[13px] text-muted-foreground">Vous</span>
                      ) : (
                        <button type="button" onClick={() => setSelectedId(u.id)} className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[13px] font-bold text-primary">
                          {u.teams.length === 0 ? 'Rattacher' : "Changer d'équipe"}
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </SectionCard>

          <SectionCard title="Ce que permet chaque rôle">
            <Table minWidth={640}>
              <TableHeader>
                <TableRow>
                  <TableHead>Action</TableHead>
                  <TableHead>Demandeur</TableHead>
                  <TableHead>Référent</TableHead>
                  <TableHead>PO</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {MATRIX.map(([action, ...cells]) => (
                  <TableRow key={action}>
                    <TableCell>{action}</TableCell>
                    {cells.map((c, i) => (
                      <TableCell key={i} className="text-foreground-secondary">
                        {c}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </SectionCard>
        </div>
        {selected && (
          <aside className="flex min-w-0 flex-[1_1_320px] flex-col">
            <AttachPanel key={selected.id + selected.teams.length} user={selected} teams={data?.teams ?? []} onClose={() => setSelectedId(null)} />
          </aside>
        )}
      </div>
    </>
  );
}
